import { CircularProgress, IconButton, Menu, MenuItem } from '@material-ui/core'
import BusinessIcon from '@material-ui/icons/Business'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
	useFetchUserInfoQuery,
	useUpdateCurrentCompanyUserInfoMutation,
} from 'src/redux/api/graphql/userInfoApi'
import { setUserInfoCompanyTenantId } from 'src/redux/slices/auth'
import { clearProjectsData } from 'src/redux/slices/projects'
import {
	getCompanyNames,
	setIsChooseCompanyMenuOpen,
} from 'src/redux/slices/settings'
import type { CompanyNameType } from 'src/types/miscType'
import {
	useAppDispatch,
	useAppSelector,
	useAppThunkDispatch,
} from 'src/utils/hooks/reduxHooks'

function ChooseCompanyForCompanyMultipleTenantAdminOrFreelancerButton() {
	const { t } = useTranslation()
	const { t: tLanguages } = useTranslation('languages')
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const dispatchThunk = useAppThunkDispatch()
	const companyMenuRef = useRef<HTMLButtonElement>(null)
	const userTableInfo = useAppSelector((state) => state.auth.userTableInfo)
	const companyNames = useAppSelector((state) => state.settings.companyNames)
	const isChooseCompanyMenuOpen = useAppSelector(
		(state) => state.settings.isChooseCompanyMenuOpen
	)
	const fetchCompanyNameApiStatus = useAppSelector(
		(state) => state.settings.fetchCompanyNameApiStatus
	)
	const userLoginStatus = useAppSelector(
		(state) => state.auth.userLoginStatus
	)
	const isFreelancer = userTableInfo?.cognitoGroup?.includes('freelancer')
	const isCompanyMultipleTenantAdminOrFreelancer =
		userTableInfo?.cognitoGroup?.includes('companyMultipleTenantAdmin') ||
		userTableInfo?.cognitoGroup?.includes('freelancer')
	const freelancerTenantId = userTableInfo?.tenantId

	const { data: userInfoData, isLoading } = useFetchUserInfoQuery(
		{
			userInfoId: userTableInfo?.id!,
		},
		{
			skip:
				!isCompanyMultipleTenantAdminOrFreelancer ||
				!userLoginStatus ||
				!userTableInfo?.id,
		}
	)
	const [updateCurrentCompany] = useUpdateCurrentCompanyUserInfoMutation()

	const currentCompanyName = userInfoData?.currentCompanyName || 'No Name'
	const currentCompanyTenantId = userInfoData?.currentCompanyTenantId
	const companyTenantIds = userInfoData?.companyTenantId
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
	const [isOpen, setIsOpen] = useState(false)

	// Fetch company details when component mounts and company tenant IDs are available
	// This is a legitimate data-fetching effect that runs when dependencies change
	// biome-ignore lint: Legitimate data-fetching effect for company names
	useEffect(
		function getCompanyDetails() {
			if (
				!!companyMenuRef.current &&
				Array.isArray(companyTenantIds) &&
				companyTenantIds.every((val) => typeof val === 'string') &&
				!(companyNames?.length > 0)
			) {
				dispatchThunk(getCompanyNames(companyTenantIds as string[]))
			}
		},
		[companyNames?.length, companyTenantIds, dispatchThunk]
	)

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setIsOpen(!isOpen)
		// dispatch(setIsUserMenuOpen(true))
	}

	const handleClose = () => {
		setIsOpen(false)
		dispatch(setIsChooseCompanyMenuOpen(false))
	}
	function changeCurrentCompany({ tenantId, name }: CompanyNameType) {
		if (userTableInfo?.id) {
			// Set timestamp to prevent automatic updates from other components
			;(window as any).lastCompanyChangeTime = Date.now()

			updateCurrentCompany({ tenantId, name, userInfo: userTableInfo })
			dispatch(setUserInfoCompanyTenantId(tenantId))
			dispatch(clearProjectsData())
			navigate('/')
			setIsOpen(false)
			dispatch(setIsChooseCompanyMenuOpen(false))
		}
	}

	function listCompanies() {
		return userInfoData?.companyTenantId?.map((c) => {
			if (c !== userInfoData?.currentCompanyTenantId && c) {
				const companyInfo = companyNames.find(
					(element) => element.tenantId === c
				)
				const companyInfoArg = companyInfo || {
					tenantId: c,
					name: `Company Name not provided ${c.substring(0, 4)}`,
				}
				return (
					<MenuItem
						key={c}
						onClick={() => changeCurrentCompany(companyInfoArg)}
					>
						{companyInfo?.name ||
							`Company Name not provided ${c.substring(0, 4)}`}
					</MenuItem>
				)
			}
			return <></>
		})
	}

	function checkIfIsLoading() {
		if (isLoading || fetchCompanyNameApiStatus === 'loading') {
			return false
		}
		return true
	}

	return (
		<>
			<IconButton
				aria-controls="simple-menu"
				aria-haspopup="true"
				onClick={handleClick}
				style={{ color: 'white', textTransform: 'capitalize' }}
				className="listOfCompaniesIcon"
				ref={companyMenuRef}
			>
				<BusinessIcon />
			</IconButton>
			<Menu
				id="simple-menu"
				aria-label="Choose Current Company"
				anchorEl={companyMenuRef.current}
				keepMounted
				open={isChooseCompanyMenuOpen || isOpen}
				onClose={handleClose}
				PaperProps={{
					className: 'listOfCompaniesContent',
				}}
			>
				<MenuItem style={{ backgroundColor: '#d4d4d4' }}>
					{' '}
					{currentCompanyName}
				</MenuItem>
				{isFreelancer &&
					currentCompanyTenantId !== freelancerTenantId &&
					freelancerTenantId && (
						<MenuItem
							onClick={() =>
								changeCurrentCompany({
									tenantId: freelancerTenantId,
									name: tLanguages('My Projects'),
								})
							}
						>
							{tLanguages('My Projects')}
						</MenuItem>
					)}
				{checkIfIsLoading() && listCompanies()}
				{!checkIfIsLoading() && (
					<MenuItem style={{ display: 'grid', placeItems: 'center' }}>
						<CircularProgress />
					</MenuItem>
				)}
			</Menu>
		</>
	)
}

export default ChooseCompanyForCompanyMultipleTenantAdminOrFreelancerButton
